import { useState } from "react";
import { clientMethods, useAuthUi } from "../context";
import { ActionButton, AuthShell, Divider, Form, ResultNotice, SubmitButton, TextField, useAction } from "../components";
import type { AuthPageProps, ConsentRequest, EmailAction } from "../types";

export function SignInPage({ searchParams }: AuthPageProps) {
  const { api, client, locale, t } = useAuthUi();
  const methods = clientMethods(client);
  const [email, setEmail] = useState(() => signedLoginHint(searchParams));
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const action = useAction();
  const returnTo = searchParams?.get("return_to") ?? undefined;
  const magicLinkHref = authRouteWithSearch("/magic-link", searchParams);
  const forgotPasswordHref = authRouteWithSearch("/forgot-password", searchParams);
  const signUpHref = authRouteWithSearch("/sign-up", searchParams);
  const callbackError = searchParams?.has("error")
    ? { ok: false, message: t("socialSignInFailed") }
    : null;
  const interactionExpired = searchParams?.has("interaction_expired")
    ? { ok: false, message: t("authorizationExpired") }
    : null;
  const useProvider = async (provider: "google" | "passkey") => {
    if (provider === "google") await action.run(() => api.signInGoogle(returnTo ? { returnTo } : {}));
    else await action.run(() => api.signInPasskey(email ? { email } : {}));
  };
  return <AuthShell title={t("signInTo", { app: client.name })}>
    {methods.has("password") ? <Form onSubmit={() => action.run(() => api.signInPassword({ email, password, remember }))}>
      <TextField label={t("email")} name="email" type="email" autoComplete="username" inputMode="email" required value={email} onChange={(event) => setEmail(event.target.value)} error={action.result?.fieldErrors?.email} />
      <TextField label={t("password")} name="password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} error={action.result?.fieldErrors?.password} />
      <div className="form-row form-row--spread"><label className="check"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} /> {t("remember")}</label><a href={forgotPasswordHref}>{t("forgotPassword")}</a></div>
      <SubmitButton busy={action.busy}>{t("signIn")}</SubmitButton>
    </Form> : null}
    {(methods.has("google") || methods.has("passkey") || methods.has("magic-link")) && methods.has("password") ? <Divider label={locale === "en" ? "or" : "ou"} /> : null}
    <div className="provider-list">
      {methods.has("google") ? <ActionButton busy={action.busy} onClick={() => void useProvider("google")}><span aria-hidden="true">G</span> {t("google")}</ActionButton> : null}
      {methods.has("passkey") ? <ActionButton busy={action.busy} onClick={() => void useProvider("passkey")}><span aria-hidden="true">◇</span> {t("passkey")}</ActionButton> : null}
      {methods.has("magic-link") ? <a className="button button--secondary" href={magicLinkHref}>{t("magicLink")}</a> : null}
    </div>
    <ResultNotice result={action.result ?? callbackError ?? interactionExpired} />
    <p className="switch-page">{t("noAccount")} <a href={signUpHref}>{t("signUp")}</a></p>
  </AuthShell>;
}

export function SignUpPage({ searchParams }: AuthPageProps) {
  const { api, client, locale, t } = useAuthUi();
  const methods = clientMethods(client);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => signedLoginHint(searchParams));
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const action = useAction();
  const returnTo = searchParams?.get("return_to") ?? undefined;
  if (!methods.has("password") && !methods.has("google")) {
    return <SignInPage searchParams={searchParams} />;
  }
  async function submit() {
    if (password !== confirmation) {
      action.setResult({ ok: false, message: t("passwordsDiffer"), fieldErrors: { confirmation: t("passwordsDiffer") } });
      return;
    }
    await action.run(() => api.signUp({ name, email, password, locale }));
  }
  return <AuthShell title={t("createFor", { app: client.name })}>
    {methods.has("password") ? <Form onSubmit={submit}>
      <TextField label={t("name")} name="name" autoComplete="name" required value={name} onChange={(event) => setName(event.target.value)} error={action.result?.fieldErrors?.name} />
      <TextField label={t("email")} name="email" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(event) => setEmail(event.target.value)} error={action.result?.fieldErrors?.email} />
      <TextField label={t("password")} name="password" type="password" autoComplete="new-password" minLength={8} maxLength={128} required value={password} onChange={(event) => setPassword(event.target.value)} hint={t("passwordHint")} error={action.result?.fieldErrors?.password} />
      <TextField label={t("confirmPassword")} name="password-confirmation" type="password" autoComplete="new-password" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} error={action.result?.fieldErrors?.confirmation} />
      <SubmitButton busy={action.busy}>{t("signUp")}</SubmitButton>
    </Form> : null}
    {methods.has("google") && methods.has("password") ? <Divider label={locale === "en" ? "or" : "ou"} /> : null}
    {methods.has("google") ? <div className="provider-list">
      <ActionButton
        busy={action.busy}
        onClick={() => void action.run(() => api.signInGoogle({
          ...(returnTo ? { returnTo } : {}),
          requestSignUp: true,
        }))}
      >
        <span aria-hidden="true">G</span> {t("signUpGoogle")}
      </ActionButton>
    </div> : null}
    <ResultNotice result={action.result} />
    <p className="switch-page">{t("haveAccount")} <a href={authRouteWithSearch("/sign-in", searchParams)}>{t("signIn")}</a></p>
  </AuthShell>;
}

function EmailRequestPage({ kind, searchParams }: { kind: "magic" | "reset"; searchParams?: URLSearchParams | undefined }) {
  const { api, locale, t } = useAuthUi();
  const [email, setEmail] = useState(() => signedLoginHint(searchParams));
  const action = useAction();
  const submit = async () => {
    const result = await action.run(() => kind === "magic" ? api.requestMagicLink({ email, locale }) : api.requestPasswordReset({ email, locale }));
    if (result.ok) action.setResult({ ok: true, message: t("genericEmailSent") });
  };
  return <AuthShell title={t(kind === "magic" ? "magicTitle" : "forgotTitle")} intro={t(kind === "magic" ? "magicIntro" : "forgotIntro")}>
    <Form onSubmit={submit}>
      <TextField label={t("email")} type="email" autoComplete="email" inputMode="email" required value={email} onChange={(event) => setEmail(event.target.value)} error={action.result?.fieldErrors?.email} />
      <SubmitButton busy={action.busy}>{t(kind === "magic" ? "sendLink" : "forgotPassword")}</SubmitButton>
    </Form>
    <ResultNotice result={action.result} />
    <p className="switch-page"><a href={authRouteWithSearch("/sign-in", searchParams)}>{t("backToSignIn")}</a></p>
  </AuthShell>;
}

export function MagicLinkRequestPage({ searchParams }: AuthPageProps) { return <EmailRequestPage kind="magic" searchParams={searchParams} />; }
export function ForgotPasswordPage({ searchParams }: AuthPageProps) { return <EmailRequestPage kind="reset" searchParams={searchParams} />; }

export function ResetPasswordPage({ searchParams }: AuthPageProps) {
  const { api, t } = useAuthUi();
  const token = searchParams?.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const action = useAction();
  async function submit() {
    if (!token) return action.setResult({ ok: false, message: t("invalidLink") });
    if (password !== confirmation) return action.setResult({ ok: false, message: t("passwordsDiffer") });
    await action.run(() => api.resetPassword({ token, password }));
  }
  return <AuthShell title={t("resetTitle")}>
    {!token ? <div className="notice notice--error" role="alert">{t("invalidLink")}</div> : <Form onSubmit={submit}>
      <TextField label={t("newPassword")} type="password" autoComplete="new-password" minLength={8} maxLength={128} required value={password} onChange={(event) => setPassword(event.target.value)} hint={t("passwordHint")} error={action.result?.fieldErrors?.password} />
      <TextField label={t("confirmPassword")} type="password" autoComplete="new-password" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
      <SubmitButton busy={action.busy}>{t("resetAction")}</SubmitButton>
    </Form>}
    <ResultNotice result={action.result} />
  </AuthShell>;
}

function ConfirmEmailActionPage({ type, searchParams }: { type: EmailAction; searchParams?: URLSearchParams | undefined }) {
  const { api, t } = useAuthUi();
  const token = searchParams?.get("token") ?? "";
  const action = useAction();
  const magic = type === "magic-link";
  return <AuthShell title={t(magic ? "magicConfirmTitle" : "verifyTitle")} intro={t(magic ? "magicConfirmIntro" : "verifyIntro")}>
    {!token ? <div className="notice notice--error" role="alert">{t("invalidLink")}</div> : <Form onSubmit={() => action.run(() => api.consumeEmailAction({ type, token }))}>
      <SubmitButton busy={action.busy}>{t(magic ? "magicConfirmAction" : "verifyAction")}</SubmitButton>
    </Form>}
    <ResultNotice result={action.result} />
  </AuthShell>;
}

export function VerifyEmailPage({ searchParams }: AuthPageProps) { return <ConfirmEmailActionPage type="verify-email" searchParams={searchParams} />; }
export function MagicLinkConfirmationPage({ searchParams }: AuthPageProps) { return <ConfirmEmailActionPage type="magic-link" searchParams={searchParams} />; }

export function authRouteWithSearch(path: string, searchParams?: URLSearchParams): string {
  const signed = signedOAuthSearchParams(searchParams);
  if (signed) return `${path}?${signed}`;

  const safe = new URLSearchParams();
  for (const name of ["client_id", "ui_locales", "error", "interaction_expired"]) {
    const values = searchParams?.getAll(name) ?? [];
    if (values.length === 1 && values[0]) safe.set(name, values[0]);
  }
  const query = safe.toString();
  return query ? `${path}?${query}` : path;
}

function signedLoginHint(searchParams?: URLSearchParams): string {
  const signed = signedOAuthSearchParams(searchParams);
  if (!signed) return "";
  const hints = new URLSearchParams(signed).getAll("login_hint");
  const hint = hints.length === 1 ? hints[0] ?? "" : "";
  return hint.length <= 320 ? hint : "";
}

function signedOAuthSearchParams(searchParams?: URLSearchParams): string | undefined {
  if (!searchParams) return undefined;
  const signatures = searchParams.getAll("sig");
  const signedNames = searchParams.getAll("ba_param");
  if (
    signatures.length !== 1
    || !signatures[0]
    || signedNames.length === 0
    || !signedNames.includes("ba_param")
  ) {
    return undefined;
  }

  const allowlist = new Set(signedNames);
  const signed = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (key === "sig" || key === "ba_param" || allowlist.has(key)) {
      signed.append(key, value);
    }
  }
  return signed.toString();
}

const scopeKeys = {
  openid: "scopeOpenid",
  profile: "scopeProfile",
  email: "scopeEmail",
  offline_access: "scopeOffline"
} as const;

export function ConsentPage({ request }: { request?: ConsentRequest | undefined }) {
  const { api, t } = useAuthUi();
  const action = useAction();
  if (!request) return <AuthShell title={t("consentTitle", { app: "Elefante" })}><div className="notice notice--error" role="alert">{t("invalidLink")}</div></AuthShell>;
  const respond = (accept: boolean) => action.run(() => api.respondToConsent({ requestId: request.id, accept }));
  return <AuthShell title={t("consentTitle", { app: request.client.name })} intro={t("consentIntro")}>
    <ul className="scope-list">{request.scopes.map((scope) => <li key={scope}>{scope in scopeKeys ? t(scopeKeys[scope as keyof typeof scopeKeys]) : scope}</li>)}</ul>
    <div className="button-row"><ActionButton busy={action.busy} tone="primary" onClick={() => void respond(true)}>{t("allow")}</ActionButton><ActionButton busy={action.busy} onClick={() => void respond(false)}>{t("deny")}</ActionButton></div>
    <ResultNotice result={action.result} />
  </AuthShell>;
}
